// InspectionLens — the "what the agent knows" mode: three lenses, one door.
//
// The power-audit half of the surface (view-agent-inspection.md §2): three
// lenses answering three altitudes of one question — about ME (Knows you) /
// about the SUBJECT MATTER (Knowledge bank) / about THIS MATERIAL (Grounding).
// All read-only projections over the same log. A SegmentedControl switches
// between them (exclusive choice, roving-tabindex a11y from the primitive).
//
// Pure/props-driven: each lens' resolved data + handlers arrive as props; the
// only state is which sub-lens is active (ephemeral view-state).

export type { InspectionLensProps, InspectionSubLens };
export { InspectionLens };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { SegmentedControl } from "../../SegmentedControl/SegmentedControl.js";
import type {
    GroundingCoverage,
    GroundingElementRow,
    KbTriple,
    KnowsYou as KnowsYouData,
    MemoryMilestone,
} from "../types.js";
import { GroundingLens } from "./GroundingLens.js";
import { KnowledgeBank } from "./KnowledgeBank.js";
import { KnowsYou } from "./KnowsYou.js";
import "./Inspection.css";

type InspectionSubLens = "knows_you" | "knowledge_bank" | "grounding";

type InspectionLensProps = {
    // Knows-you (M1/M2/M3).
    readonly knowsYou: KnowsYouData;
    readonly onRevealMilestone?: (milestone: MemoryMilestone) => void;
    // Knowledge-bank.
    readonly triples: readonly KbTriple[];
    readonly onOpenEntity?: (key: string) => void;
    // Grounding.
    readonly groundingMaterialTitle?: string;
    readonly groundingElements: readonly GroundingElementRow[];
    readonly groundingCoverage: GroundingCoverage;
    readonly onJump?: (elementId: string, anchorIndex: number) => void;
    readonly onLinkSource?: (elementId: string) => void;
    // Which sub-lens to open first — the "Why did the agent write this?" door
    // opens Grounding, the "Show what the agent knows" door opens Knows-you.
    readonly initial?: InspectionSubLens;
};

const InspectionLens = ({
    knowsYou,
    onRevealMilestone,
    triples,
    onOpenEntity,
    groundingMaterialTitle,
    groundingElements,
    groundingCoverage,
    onJump,
    onLinkSource,
    initial = "knows_you",
}: InspectionLensProps): ReactNode => {
    const { t } = useLingui();
    const [sub, setSub] = useState<InspectionSubLens>(initial);

    const options = [
        { value: "knows_you", label: t`Knows you` },
        { value: "knowledge_bank", label: t`Uploaded sources` },
        { value: "grounding", label: t`Grounding` },
    ];

    return (
        <div className="bilda-hi-inspection">
            <SegmentedControl
                options={options}
                value={sub}
                onChange={(value) => setSub(asSubLens(value))}
                ariaLabel={t`Choose an inspection lens`}
                size="sm"
            />
            <div className="bilda-hi-inspection__body">
                {sub === "knows_you" && (
                    <KnowsYou
                        data={knowsYou}
                        onRevealMilestone={onRevealMilestone}
                    />
                )}
                {sub === "knowledge_bank" && (
                    <KnowledgeBank
                        triples={triples}
                        onOpenEntity={onOpenEntity}
                    />
                )}
                {sub === "grounding" && (
                    <GroundingLens
                        materialTitle={groundingMaterialTitle}
                        elements={groundingElements}
                        coverage={groundingCoverage}
                        onJump={onJump}
                        onLinkSource={onLinkSource}
                    />
                )}
            </div>
        </div>
    );
};

// Narrow the SegmentedControl's string back to the sub-lens sum — a guard, not
// a cast (the control only ever emits one of our option values).
const asSubLens = (value: string): InspectionSubLens => {
    switch (value) {
        case "knowledge_bank":
            return "knowledge_bank";
        case "grounding":
            return "grounding";
        default:
            return "knows_you";
    }
};
